/**
 * UAPC Anytime - Landing Page
 * @module LandingPage
 * @author Osman - Frontend Developer
 * @author Roohi Fatima - Frontend Developer
 * @description Main landing page with hero, search, and features
 * @version 1.0.0
 * Git: Osman <osman@linesoft.com> - Landing page layout
 * Git: Roohi Fatima <roohi@linesoft.com> - Hero section
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ShoppingCart, User, Truck, Shield, Clock, ChevronRight, Menu, X } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LandingPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { itemCount } = useCart();
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState([]);
  const [featuredParts, setFeaturedParts] = useState([]);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchCategories();
    fetchFeaturedParts();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/categories`);
      setCategories(response.data.categories?.slice(0, 8) || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  };

  const fetchFeaturedParts = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/parts?limit=8`);
      setFeaturedParts(response.data || []);
    } catch (error) {
      console.error('Failed to fetch parts:', error);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  const categoryImages = {
    'Engine': 'https://images.unsplash.com/photo-1486262715619-67b85e0b08d3?auto=format&fit=crop&q=80&w=400',
    'Brakes': 'https://images.unsplash.com/photo-1767480350992-d9e35ce04410?auto=format&fit=crop&q=80&w=400',
    'Transmission': 'https://images.unsplash.com/photo-1613039704499-1975f634d75f?auto=format&fit=crop&q=80&w=400',
    'Suspension': 'https://images.unsplash.com/photo-1760317890359-4e6bb111e501?auto=format&fit=crop&q=80&w=400',
    'Electrical': 'https://images.unsplash.com/photo-1666537072037-642bb30cb767?auto=format&fit=crop&q=80&w=400',
    'Body Parts': 'https://images.unsplash.com/photo-1563720223185-11003d516935?auto=format&fit=crop&q=80&w=400',
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950" data-testid="landing-page">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2" data-testid="logo-link">
              <div className="w-10 h-10 bg-blue-600 flex items-center justify-center">
                <span className="font-heading font-black text-xl">U</span>
              </div>
              <span className="font-heading font-bold text-xl tracking-tight hidden sm:block">UAPC ANYTIME</span>
            </Link>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-6">
              <Link to="/search" className="text-slate-300 hover:text-white transition-colors" data-testid="nav-search">
                Search Parts
              </Link>
              {user?.role === 'vendor' && (
                <Link to="/vendor" className="text-slate-300 hover:text-white transition-colors" data-testid="nav-vendor">
                  Vendor Portal
                </Link>
              )}
              {user?.role === 'admin' && (
                <Link to="/admin" className="text-slate-300 hover:text-white transition-colors" data-testid="nav-admin">
                  Admin
                </Link>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center gap-4">
              {user ? (
                <>
                  <Link to="/cart" className="relative" data-testid="cart-link">
                    <ShoppingCart className="w-6 h-6" />
                    {itemCount > 0 && (
                      <span className="absolute -top-2 -right-2 w-5 h-5 bg-orange-500 rounded-full text-xs flex items-center justify-center font-bold">
                        {itemCount}
                      </span>
                    )}
                  </Link>
                  <div className="hidden md:flex items-center gap-3">
                    <Link to="/orders" className="text-slate-300 hover:text-white text-sm">
                      Orders
                    </Link>
                    <button
                      onClick={logout}
                      className="text-slate-300 hover:text-white text-sm"
                      data-testid="logout-btn"
                    >
                      Logout
                    </button>
                  </div>
                </>
              ) : (
                <div className="hidden md:flex items-center gap-3">
                  <Link to="/login" data-testid="login-link">
                    <Button variant="ghost" className="text-white hover:text-blue-400">
                      Login
                    </Button>
                  </Link>
                  <Link to="/register" data-testid="register-link">
                    <Button className="bg-blue-600 hover:bg-blue-700">
                      Register
                    </Button>
                  </Link>
                </div>
              )}
              
              {/* Mobile menu button */}
              <button
                className="md:hidden p-2"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                data-testid="mobile-menu-btn"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-slate-800 px-4 py-4">
            <div className="flex flex-col gap-4">
              <Link to="/search" className="text-slate-300 hover:text-white">Search Parts</Link>
              {user?.role === 'vendor' && (
                <Link to="/vendor" className="text-slate-300 hover:text-white">Vendor Portal</Link>
              )}
              {user?.role === 'admin' && (
                <Link to="/admin" className="text-slate-300 hover:text-white">Admin</Link>
              )}
              {user ? (
                <>
                  <Link to="/orders" className="text-slate-300 hover:text-white">Orders</Link>
                  <button onClick={logout} className="text-slate-300 hover:text-white text-left">Logout</button>
                </>
              ) : (
                <>
                  <Link to="/login" className="text-slate-300 hover:text-white">Login</Link>
                  <Link to="/register" className="text-slate-300 hover:text-white">Register</Link>
                </>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section 
        className="relative pt-16 min-h-[600px] flex items-center"
        style={{
          backgroundImage: 'linear-gradient(to right, rgba(15, 23, 42, 0.95), rgba(15, 23, 42, 0.7)), url(https://images.unsplash.com/photo-1613039704499-1975f634d75f?q=80&w=2070&auto=format&fit=crop)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-24">
          <div className="max-w-2xl">
            <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl text-white uppercase tracking-tighter animate-fade-in-up">
              AUTO PARTS
              <span className="block text-blue-500">ANYTIME</span>
            </h1>
            <p className="mt-6 text-lg text-slate-300 font-body leading-relaxed animate-fade-in-up animate-delay-100">
              Houston's premier multi-vendor auto parts marketplace. Search across multiple junkyards 
              and vendors to find the exact part you need.
            </p>
            
            {/* Search Form */}
            <form onSubmit={handleSearch} className="mt-8 flex gap-0 animate-fade-in-up animate-delay-200">
              <div className="relative flex-1">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  type="text"
                  placeholder="Search by part number, name, or vehicle..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="h-14 pl-12 bg-white border-0 text-slate-900 placeholder:text-slate-500 text-lg"
                  data-testid="hero-search-input"
                />
              </div>
              <Button 
                type="submit" 
                className="h-14 px-8 bg-orange-500 hover:bg-orange-600 font-heading font-bold uppercase tracking-wider"
                data-testid="hero-search-btn"
              >
                Search
              </Button>
            </form>

            {/* Quick Stats */}
            <div className="mt-12 flex gap-8 animate-fade-in-up animate-delay-300">
              <div>
                <div className="font-heading font-black text-3xl text-white">10K+</div>
                <div className="text-slate-400 text-sm uppercase tracking-wider">Parts Listed</div>
              </div>
              <div>
                <div className="font-heading font-black text-3xl text-white">50+</div>
                <div className="text-slate-400 text-sm uppercase tracking-wider">Vendors</div>
              </div>
              <div>
                <div className="font-heading font-black text-3xl text-white">24/7</div>
                <div className="text-slate-400 text-sm uppercase tracking-wider">Availability</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-slate-50 dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-heading font-bold text-3xl text-slate-900 dark:text-white uppercase text-center mb-12">
            Why Choose UAPC?
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-slate-800 p-8 border border-slate-200 dark:border-slate-700">
              <div className="w-12 h-12 bg-blue-600 flex items-center justify-center mb-4">
                <Truck className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-900 dark:text-white uppercase mb-2">
                Fast Shipping
              </h3>
              <p className="text-slate-600 dark:text-slate-400 font-body">
                Get your parts delivered quickly with our network of Houston-area vendors.
              </p>
            </div>
            <div className="bg-white dark:bg-slate-800 p-8 border border-slate-200 dark:border-slate-700">
              <div className="w-12 h-12 bg-blue-600 flex items-center justify-center mb-4">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-900 dark:text-white uppercase mb-2">
                Quality Guaranteed
              </h3>
              <p className="text-slate-600 dark:text-slate-400 font-body">
                All parts are verified and backed by our quality assurance program.
              </p>
            </div>
            <div className="bg-white dark:bg-slate-800 p-8 border border-slate-200 dark:border-slate-700">
              <div className="w-12 h-12 bg-blue-600 flex items-center justify-center mb-4">
                <Clock className="w-6 h-6 text-white" />
              </div>
              <h3 className="font-heading font-bold text-xl text-slate-900 dark:text-white uppercase mb-2">
                Real-Time Inventory
              </h3>
              <p className="text-slate-600 dark:text-slate-400 font-body">
                Live sync with vendor inventory ensures accurate availability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Categories Section */}
      {categories.length > 0 && (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-heading font-bold text-3xl text-slate-900 dark:text-white uppercase mb-8">
              Browse Categories
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
              {categories.map((category, index) => (
                <Link
                  key={category}
                  to={`/search?category=${encodeURIComponent(category)}`}
                  className="group relative h-40 overflow-hidden border border-slate-200 dark:border-slate-700"
                  data-testid={`category-${index}`}
                >
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                    style={{
                      backgroundImage: `url(${categoryImages[category] || 'https://images.unsplash.com/photo-1613039704499-1975f634d75f?auto=format&fit=crop&q=80&w=400'})`
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-900 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="font-heading font-bold text-lg text-white uppercase">
                      {category}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Featured Parts */}
      {featuredParts.length > 0 && (
        <section className="py-20 bg-slate-50 dark:bg-slate-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-between mb-8">
              <h2 className="font-heading font-bold text-3xl text-slate-900 dark:text-white uppercase">
                Featured Parts
              </h2>
              <Link 
                to="/search" 
                className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-bold uppercase text-sm"
              >
                View All <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 stagger-children">
              {featuredParts.map((part, index) => (
                <Link
                  key={part.id}
                  to={`/parts/${part.id}`}
                  className="group bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 hover:border-blue-500 transition-colors animate-fade-in-up"
                  data-testid={`featured-part-${index}`}
                >
                  <div className="aspect-square bg-slate-100 dark:bg-slate-700 mb-4 flex items-center justify-center overflow-hidden">
                    {part.image_url ? (
                      <img src={part.image_url} alt={part.name} className="w-full h-full object-cover" />
                    ) : (
                      <span className="text-slate-400 font-mono text-sm">{part.part_number}</span>
                    )}
                  </div>
                  <div className="font-mono text-xs text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
                    {part.part_number}
                  </div>
                  <h3 className="font-heading font-bold text-lg text-slate-900 dark:text-white line-clamp-2">
                    {part.name}
                  </h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="font-heading font-bold text-xl text-blue-600">
                      ${part.price.toFixed(2)}
                    </span>
                    <span className={`px-2 py-1 text-xs font-bold uppercase ${
                      part.condition === 'new' ? 'bg-green-100 text-green-800' :
                      part.condition === 'refurbished' ? 'bg-blue-100 text-blue-800' :
                      'bg-slate-100 text-slate-800'
                    }`}>
                      {part.condition}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA Section */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-black text-4xl text-white uppercase mb-4">
            Become a Vendor
          </h2>
          <p className="text-slate-400 text-lg max-w-2xl mx-auto mb-8">
            Join Houston's fastest-growing auto parts marketplace. List your inventory and reach 
            thousands of customers.
          </p>
          <Link to="/register" data-testid="cta-register">
            <Button className="btn-industrial">
              Register as Vendor <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-slate-950 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-blue-600 flex items-center justify-center">
                <span className="font-heading font-black text-sm text-white">U</span>
              </div>
              <span className="font-heading font-bold text-white">UAPC ANYTIME</span>
            </div>
            <div className="text-slate-500 text-sm">
              © 2024 Unlimited Auto Parts Company. All rights reserved.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
